import { useQuery } from '@apollo/client';
import { getDataFromTree } from '@apollo/client/react/ssr';
import { NextPage } from 'next';
import { ApolloPageContext, PageProps } from 'helpers/next-page';
// Api
import { GET_ARTICLES } from 'api/articles/queries';
// Types
import {
  GetArticleForStore,
  GetArticleForStoreVariables,
} from 'api/articles/types/GetArticleForStore';
import {
  ArticleStatus,
  ArticleStoreOrderBy,
  SortDirection,
} from 'api/graphql-global-types';
// Helpers
import { getAthleteTabPageProps } from 'helpers/athleteTabPageGuard';
import { getEnvLink } from 'helpers/routes';
import { generateItemListStructuredData } from 'helpers/structured-data';
// Hocks
import withApollo from 'hocs/withApollo';
// Hooks
import {
  useRedirectOnError,
  useRedirectIfNoContent,
  useAthletePageData,
} from 'hooks';
// Layouts
import { getProfileAvatarPreload } from 'helpers/profileAvatar';
import Head from 'layouts/Head/Head';
import Header from 'layouts/Header/Header';
import Main from 'layouts/Main/Main';
import Footer from 'layouts/Footer/Footer';
// Components
import AppBannerAndModal from 'components/common3/AppBannerAndModal/AppBannerAndModal';
import Description from 'components/Athlete/Description/Description';
import { AllNews } from 'components/AllNews/AllNews';
import { FiltersProvider } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
import NotFoundPage from 'components/NotFoundPage/NotFoundPage';

const ProductsContent = (): JSX.Element | null => {
  const {
    error,
    skip,
    cleanedSlug,
    athleteSlug,
    metaData,
    store,
  } = useAthletePageData();

  const { data: articlesData } = useQuery<
    GetArticleForStore,
    GetArticleForStoreVariables
  >(GET_ARTICLES, {
    variables: {
      input: {
        storeId: store?.id || '',
        statuses: [ArticleStatus.Active],
      },
    },
    skip: !store,
  });

  const articles = articlesData?.getArticleForStore?.entities;

  useRedirectOnError({ error, redirectTo: `/${athleteSlug}` });

  // Redirect to profile home if articles content is not available
  useRedirectIfNoContent({
    hasContent: store?.hasArticle ?? false,
    redirectTo: `/${athleteSlug}`,
    skip: !store || skip,
  });

  if (!store) {
    return null;
  }

  const storeId = store.id;

  const canonicalLink = `${getEnvLink()}/${cleanedSlug}/news`;

  const newsItems =
    articles
      ?.filter((item) => item.store?.slug)
      .map((item) => ({
        url: `${getEnvLink()}/${item.store?.slug}/news/${item.slug}`,
      })) || [];

  const structuredData = generateItemListStructuredData(
    `${metaData.name} News`,
    newsItems
  );

  return (
    <>
      <Head
        title={`${metaData.name} News & Articles | MILLIONS`}
        description={`Read the latest news and articles about ${metaData.name} on MILLIONS.`}
        image={metaData.avatarUrl}
        imagePreload={getProfileAvatarPreload(metaData.avatarUrl)}
        canonicalLink={canonicalLink}
        robots={!store.hasArticle ? 'noindex, follow' : undefined}
        structuredData={structuredData}
      />
      <AppBannerAndModal
        when
        variant="profile"
        username={cleanedSlug ?? undefined}
        profileImageUrl={metaData.avatarUrl ?? undefined}
      />
      <Header />

      <Main backgroundColor="background-dim" withoutSidesPadding>
        <Description store={store} />
        <FiltersProvider
          searchKey="searchTerm"
          hideContentCreatorsFilter
          hideAthletesFilter
          hideOrganizationsFilter
          hasAthleteSlug
          initialSorting={{
            direction: SortDirection.DESC,
            order: ArticleStoreOrderBy.createdAt,
          }}
        >
          <AllNews storeId={storeId || ''} />
        </FiltersProvider>
      </Main>

      <Footer />
    </>
  );
};

const Products: NextPage<PageProps> = ({ isNotFound }) =>
  isNotFound ? <NotFoundPage /> : <ProductsContent />;

Products.getInitialProps = async (ctx: ApolloPageContext): Promise<PageProps> =>
  getAthleteTabPageProps(ctx, 'news');

export default withApollo(Products, { getDataFromTree });
